import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";

import { cvAdminApi } from "../api/cvAdminApi"; // <-- adjust path if needed

const uniq = (arr) => Array.from(new Set(arr));

const parseLines = (text) => {
  const raw = String(text || "")
    .split(/[\n,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return uniq(raw);
};

const getAxiosErrorMessage = (e) => {
  const data = e?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;

  // Laravel validation errors object { errors: { field: [..] } }
  if (data?.errors && typeof data.errors === "object") {
    const firstKey = Object.keys(data.errors)[0];
    const firstMsg = firstKey ? data.errors[firstKey]?.[0] : null;
    if (firstMsg) return firstMsg;
  }

  if (typeof e?.message === "string" && e.message.trim()) return e.message;
  return "Request failed.";
};

const DEFAULT_FORM = {
  scope: "all", // one | multi | all
  only_pending: true,
  only_not_started: false,

  // target
  user_id: "",
  employee_code: "",
  employee_codes_text: "",

  // window
  open_window: true,
  days: 14,
  until: "",
  reopen: false,
  note: "",

  // email
  subject: "Action Required: Please complete your CV",
  title: "CV Completion Required",
  intro: "Please complete and submit your CV in the HR system.",
  cta_text: "Open CV Form",
  cv_url: "",
};

export default function CvEmailRequests({
  notify,
  onDone,
  currentUserId = null,
  currentEmployeeCode = null,
}) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const hasCurrent = useMemo(() => !!currentUserId, [currentUserId]);

  const openDialog = useCallback(
    (preset = {}) => {
      setErr("");
      setResult(null);

      setForm(() => ({
        ...DEFAULT_FORM,
        ...(hasCurrent
          ? {
              user_id: String(currentUserId ?? ""),
              employee_code: String(currentEmployeeCode ?? ""),
            }
          : {}),
        ...preset,
      }));

      setOpen(true);
    },
    [hasCurrent, currentUserId, currentEmployeeCode]
  );

  const closeDialog = () => {
    if (sending) return;
    setOpen(false);
  };

  const setField = (key) => (e) => {
    const val = e?.target?.type === "checkbox" ? !!e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
  };

  const payload = useMemo(() => {
    const p = {
      scope: form.scope,
      only_pending: !!form.only_pending,
      only_not_started: !!form.only_not_started,

      open_window: !!form.open_window,
      reopen: !!form.reopen,
      note: form.note || null,

      subject: form.subject || null,
      title: form.title || null,
      intro: form.intro || null,
      cta_text: form.cta_text || null,
      cv_url: form.cv_url || null,
    };

    // window time
    const until = String(form.until || "").trim();
    if (until) p.until = until;
    else p.days = Number(form.days || 14);

    // scope targeting
    if (form.scope === "one") {
      const uid = String(form.user_id || "").trim();
      const code = String(form.employee_code || "").trim();

      if (uid) p.user_id = Number(uid);
      else if (code) p.employee_codes = [code];
    }

    if (form.scope === "multi") {
      const codes = parseLines(form.employee_codes_text);
      if (codes.length) p.employee_codes = codes;
    }

    return p;
  }, [form]);

  const validate = useCallback(() => {
    if (form.scope === "one") {
      const uid = String(form.user_id || "").trim();
      const code = String(form.employee_code || "").trim();
      if (!uid && !code) return "For scope=one, provide user_id or employee_code.";
    }
    if (form.scope === "multi") {
      const codes = parseLines(form.employee_codes_text);
      if (!codes.length) return "For scope=multi, provide at least one employee code.";
    }
    return "";
  }, [form]);

  const onSend = async () => {
    const v = validate();
    if (v) {
      setErr(v);
      notify?.("error", v);
      return;
    }

    setSending(true);
    setErr("");
    setResult(null);

    try {
      const data = await cvAdminApi.sendFillRequest(payload);
      setResult(data?.data || data);
      notify?.("success", "CV email request processed.");
      onDone?.();
    } catch (e) {
      const msg = getAxiosErrorMessage(e);
      setErr(msg);
      notify?.("error", msg);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (form.scope !== "multi" && form.employee_codes_text) {
      setForm((p) => ({ ...p, employee_codes_text: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scope]);

  const quickRemindPending = () => {
    openDialog({
      scope: "all",
      only_pending: true,
      only_not_started: false,
      open_window: true,
      reopen: false,
      days: 7,
    });
  };

  const quickCurrentUser = () => {
    openDialog({
      scope: "one",
      only_pending: false,
      only_not_started: false,
      open_window: true,
      reopen: false,
    });
  };

  const counts = result?.counts || result?.data?.counts;

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<CampaignIcon />}
          onClick={quickRemindPending}
          disabled={sending}
          sx={{ borderRadius: 2 }}
        >
          Remind Pending
        </Button>

        {hasCurrent ? (
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={quickCurrentUser}
            disabled={sending}
            sx={{ borderRadius: 2 }}
          >
            Email Opened User
          </Button>
        ) : null}

        <Button
          variant="contained"
          startIcon={<EmailIcon />}
          onClick={() => openDialog({})}
          disabled={sending}
          sx={{ borderRadius: 2 }}
        >
          Send CV Request
        </Button>
      </Stack>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Send CV Fill Request Emails
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Send to one employee, multiple, or all employees (filter pending / not started).
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {err ? <Alert severity="error">{err}</Alert> : null}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Scope"
                select
                value={form.scope}
                onChange={setField("scope")}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value="one">One employee</option>
                <option value="multi">Multiple employees</option>
                <option value="all">All employees</option>
              </TextField>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 320 }}>
                <FormControlLabel
                  control={<Switch checked={!!form.only_pending} onChange={setField("only_pending")} />}
                  label="Only pending (not submitted)"
                />
                <FormControlLabel
                  control={<Switch checked={!!form.only_not_started} onChange={setField("only_not_started")} />}
                  label="Only not started"
                />
              </Stack>
            </Stack>

            {form.scope === "one" ? (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="user_id (optional)"
                  value={form.user_id}
                  onChange={setField("user_id")}
                  fullWidth
                />
                <TextField
                  label="employee_code (optional)"
                  value={form.employee_code}
                  onChange={setField("employee_code")}
                  fullWidth
                />
              </Stack>
            ) : null}

            {form.scope === "multi" ? (
              <TextField
                label="Employee codes (one per line or comma-separated)"
                value={form.employee_codes_text}
                onChange={setField("employee_codes_text")}
                fullWidth
                multiline
                minRows={4}
                placeholder={`10462\n10485\n10500`}
              />
            ) : null}

            <Divider />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
              <FormControlLabel
                control={<Switch checked={!!form.open_window} onChange={setField("open_window")} />}
                label="Open CV window"
              />
              <FormControlLabel
                control={<Switch checked={!!form.reopen} onChange={setField("reopen")} />}
                label="Reopen (unlock) if already submitted"
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Days (used when 'Until' is empty)"
                type="number"
                value={form.days}
                onChange={setField("days")}
                fullWidth
                disabled={!form.open_window}
                inputProps={{ min: 1, max: 365 }}
              />
              <TextField
                label="Until (YYYY-MM-DD) optional"
                value={form.until}
                onChange={setField("until")}
                fullWidth
                disabled={!form.open_window}
                placeholder="2026-02-10"
              />
            </Stack>

            <TextField
              label="Note (optional)"
              value={form.note}
              onChange={setField("note")}
              fullWidth
              multiline
              minRows={2}
            />

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Email Content
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Subject" value={form.subject} onChange={setField("subject")} fullWidth />
              <TextField label="Button text" value={form.cta_text} onChange={setField("cta_text")} fullWidth />
            </Stack>

            <TextField label="Title" value={form.title} onChange={setField("title")} fullWidth />
            <TextField
              label="Intro"
              value={form.intro}
              onChange={setField("intro")}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="CV URL override (optional)"
              value={form.cv_url}
              onChange={setField("cv_url")}
              fullWidth
              placeholder="Leave empty to use system default CV URL"
            />

            {counts ? (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Last Run Summary
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`Targets: ${counts.targets ?? 0}`} />
                    <Chip color="success" label={`Sent: ${counts.sent ?? 0}`} />
                    <Chip label={`Skipped submitted: ${counts.skipped_submitted ?? 0}`} />
                    <Chip label={`Skipped no email: ${counts.skipped_missing_email ?? 0}`} />
                    <Chip color="error" label={`Failed: ${counts.failed ?? 0}`} />
                  </Stack>
                </Stack>
              </>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={closeDialog} disabled={sending}>
            Cancel
          </Button>

          <Button variant="contained" onClick={onSend} disabled={sending}>
            {sending ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <span>Sending...</span>
              </Stack>
            ) : (
              "Send"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
